<template>
    <UDropdownMenu :items="items" :content="{ align: 'start' }" :ui="{ content: 'w-60' }" class="w-full">
        <button
            type="button"
            class="w-full flex items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-elevated transition-colors"
            :aria-label="$t('company_switcher.label')"
        >
            <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <UIcon name="i-lucide-building-2" class="size-4" />
            </span>
            <span class="min-w-0 flex-1 leading-tight">
                <span class="block truncate text-sm font-medium text-highlighted">
                    {{ companyStore.company?.legal_name ?? $t('company_switcher.none') }}
                </span>
                <span class="block truncate font-mono text-[10px] uppercase tracking-widest text-muted">
                    {{ companyStore.company ? `${$t('company.tax_number_short')} ${companyStore.company.tax_number}` : '' }}
                </span>
            </span>
            <UBadge
                v-if="companyStore.invitations.length"
                color="primary"
                variant="solid"
                size="sm"
                :label="String(companyStore.invitations.length)"
                :aria-label="$t('company_switcher.invitations', { n: companyStore.invitations.length })"
            />
            <UIcon name="i-lucide-chevrons-up-down" class="size-4 shrink-0 text-dimmed" />
        </button>
    </UDropdownMenu>
</template>

<script lang="ts" setup>
    import type { DropdownMenuItem } from '@nuxt/ui'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import { useLayout } from '@/layouts/layout'
    import { PrintError } from '@/libs/toast'
    import { useCompanyStore } from '@/stores/company'

    const { t }         = useI18n()
    const router        = useRouter()
    const route         = useRoute()
    const companyStore  = useCompanyStore()
    const { closeMenu } = useLayout()

    const switchTo = async (id: string) => {
        if (id === companyStore.companyId) return
        closeMenu()
        const ok = await companyStore.select(id)
        if (!ok) {
            PrintError(t('company_switcher.open_failed'))
            return
        }
        // Detail pages belong to the previous company; go back to the section's list.
        // Every list page watches the selected company and reloads itself.
        if (route.name === 'import') await router.push({ name: 'imports' })
        else if (!route.meta.requiresCompany) await router.push({ name: 'machines' })
    }

    const items = computed((): DropdownMenuItem[][] => {
        const list: DropdownMenuItem[] = companyStore.companies.map((c) => ({
            label:       c.legal_name,
            description: `${t('company.tax_number_short')} ${c.tax_number}${c.status === 'blocked' ? ` · ${t('status.blocked')}` : ''}`,
            icon:        c.id === companyStore.companyId ? 'i-lucide-check' : 'i-lucide-building-2',
            disabled:    c.status === 'blocked',
            onSelect:    () => { void switchTo(c.id) },
        }))
        const current = companyStore.company
        if (current && !companyStore.companies.some((c) => c.id === current.id)) {
            list.unshift({
                label:       current.legal_name,
                description: `${t('company.tax_number_short')} ${current.tax_number} · ${t('company_switcher.admin_mode')}`,
                icon:        'i-lucide-shield',
            })
        }
        const actions: DropdownMenuItem[] = []
        if (companyStore.invitations.length) {
            actions.push({
                label:    t('company_switcher.invitations', { n: companyStore.invitations.length }),
                icon:     'i-lucide-mail',
                onSelect: () => { closeMenu(); void router.push({ name: 'company-setup' }) },
            })
        }
        actions.push({
            label:    t('company_switcher.create'),
            icon:     'i-lucide-plus',
            onSelect: () => { closeMenu(); void router.push({ name: 'company-setup', query: { create: '1' } }) },
        })
        return list.length ? [list, actions] : [actions]
    })
</script>
